import { PptSlide } from 'apps/web/types/ppt-slide';
import { jsPDF } from 'jspdf';
import PptxGenJS from 'pptxgenjs';

import {
  PPT_MAKER_THEMES,
  PPT_MAKER_FONT_OPTIONS,
  ThemeColors,
  ThemeType,
} from '@lessonPlanner/constants';

import {
  SLIDE_LAYOUT,
  PDF_LAYOUT,
} from '../../constants/pptMakerDownloadFormat';

import {
  getBackgroundImageVariation,
  setBackground,
  isTitleLong,
} from './layoutHelpers';
import { applySlideLayout } from './slideLayoutManager';
import { getLayoutBackgroundUrl } from '../slideBackgroundHelper';

interface DownloadPptProps {
  slides: PptSlide[];
  selectedTheme: { id: string };
  selectedFont: { id: string };
}

// PPTX specific functions
function createPptxTitleSlide(
  pptx: PptxGenJS,
  slide: PptSlide,
  theme: ThemeColors,
  fontId: string,
): void {
  const pptxSlide = pptx.addSlide();
  setBackground(pptxSlide, theme.backgroundImageTitle, theme.background);

  pptxSlide.addText(slide.title, {
    x: SLIDE_LAYOUT.TITLE.X_POSITION,
    y: SLIDE_LAYOUT.TITLE.Y_POSITION,
    w: SLIDE_LAYOUT.TITLE.WIDTH,
    h: SLIDE_LAYOUT.TITLE.HEIGHT,
    fontFace: fontId,
    bold: true,
    fontSize: SLIDE_LAYOUT.TITLE.FONT_SIZE,
    align: SLIDE_LAYOUT.TITLE.ALIGN,
    color: (theme.titleText ?? theme.text)?.slice(1),
  });
}

function createPptxContentSlide(
  pptx: PptxGenJS,
  slide: PptSlide,
  theme: ThemeColors,
  fontId: string,
  index: number,
  totalSlides: number,
  slides: PptSlide[],
  themeId: string,
): void {
  const pptxSlide = pptx.addSlide();
  
  let backgroundImage: string | undefined;
  
  if (themeId === ThemeType.WATER_COLOR || themeId === ThemeType.STATIONERY || themeId === ThemeType.ENERGY || themeId === ThemeType.LABORATORY || themeId === ThemeType.CIRCLE_OF_LIFE || themeId === ThemeType.SPACE || themeId === ThemeType.WEATHER || themeId === ThemeType.OFFICE || themeId === ThemeType.EVERGREEN || themeId === ThemeType.GRADIENT) {
    backgroundImage = getLayoutBackgroundUrl(themeId, index + 1, slides);
  } else {
    backgroundImage = getBackgroundImageVariation(
      theme.backgroundImageContent,
      index,
      totalSlides,
    );
  }

  setBackground(pptxSlide, backgroundImage, theme.background);
  const titleLongCheck = isTitleLong(slide.title, SLIDE_LAYOUT.CONTENT.TITLE.TITLE_LENGTH_THRESHOLD);
  const contentTextColor = (theme.contentText ?? theme.text)?.slice(1);

  // Add title
  const titleFontSize = titleLongCheck
    ? SLIDE_LAYOUT.CONTENT.TITLE.FONT_SIZE_LONG
    : SLIDE_LAYOUT.CONTENT.TITLE.FONT_SIZE_NORMAL;

  pptxSlide.addText(slide.title, {
    x: SLIDE_LAYOUT.CONTENT.TITLE.X_POSITION,
    y: titleLongCheck
      ? SLIDE_LAYOUT.CONTENT.TITLE.Y_POSITION_LONG
      : SLIDE_LAYOUT.CONTENT.TITLE.Y_POSITION,
    w: SLIDE_LAYOUT.CONTENT.TITLE.WIDTH,
    fontFace: fontId,
    bold: true,
    fontSize: titleFontSize,
    align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
    valign: SLIDE_LAYOUT.CONTENT.TEXT.VALIGN,
    color: contentTextColor,
    autoFit: true,
  });

  // Apply layout-specific content formatting
  applySlideLayout(
    pptxSlide,
    slide,
    { fontId, textColor: contentTextColor },
    true,
    titleLongCheck
  );
}

// PDF specific functions
function createPdfTitleSlide(
  doc: jsPDF,
  slide: PptSlide,
  theme: ThemeColors,
  font: string,
): void {
  const titleTextColor = theme.titleText ?? theme.text;
  doc.setTextColor(titleTextColor);

  setBackground(doc, theme.backgroundImageTitle, theme.background, false);

  doc.setFont(font, 'bold');
  doc.setFontSize(PDF_LAYOUT.TITLE.FONT_SIZE);
  const textHeight =
    (PDF_LAYOUT.TITLE.FONT_SIZE *
      (slide.title.length > PDF_LAYOUT.TITLE.TITLE_LENGTH_THRESHOLD ? 2 : 1)) /
    doc.internal.scaleFactor;
  const textYOffset = (doc.internal.pageSize.height - textHeight) / 2;
  doc.text(slide.title, PDF_LAYOUT.TITLE.X_POSITION, textYOffset, {
    align: SLIDE_LAYOUT.TITLE.ALIGN,
    maxWidth: PDF_LAYOUT.TITLE.MAX_WIDTH,
    baseline: 'middle',
  });
}

function createPdfContentSlide(
  doc: jsPDF,
  slide: PptSlide,
  theme: ThemeColors,
  font: string,
  index: number,
  totalSlides: number,
  slides: PptSlide[],
  themeId: string,
): void {
  let backgroundImage: string | undefined;
  
  if (themeId === ThemeType.WATER_COLOR || themeId === ThemeType.STATIONERY || themeId === ThemeType.ENERGY || themeId === ThemeType.LABORATORY || themeId === ThemeType.CIRCLE_OF_LIFE || themeId === ThemeType.SPACE || themeId === ThemeType.WEATHER || themeId === ThemeType.OFFICE || themeId === ThemeType.EVERGREEN || themeId === ThemeType.GRADIENT) {
    backgroundImage = getLayoutBackgroundUrl(themeId, index + 1, slides);
  } else {
    backgroundImage = getBackgroundImageVariation(
      theme.backgroundImageContent,
      index,
      totalSlides,
    );
  }

  setBackground(doc, backgroundImage, theme.background, false);
  const titleLongCheck = isTitleLong(slide.title, PDF_LAYOUT.CONTENT.TITLE.TITLE_LENGTH_THRESHOLD);
  const contentTextColor = theme.contentText ?? theme.text;
  doc.setTextColor(contentTextColor);

  // Add title
  const titleFontSize = titleLongCheck
    ? PDF_LAYOUT.CONTENT.TITLE.FONT_SIZE_LONG
    : PDF_LAYOUT.CONTENT.TITLE.FONT_SIZE_NORMAL;

  doc.setFont(font, 'bold');
  doc.setFontSize(titleFontSize);
  doc.text(
    slide.title,
    PDF_LAYOUT.CONTENT.TITLE.X_POSITION,
    titleLongCheck
      ? PDF_LAYOUT.CONTENT.TITLE.Y_POSITION_LONG
      : PDF_LAYOUT.CONTENT.TITLE.Y_POSITION,
    {
      align: SLIDE_LAYOUT.CONTENT.TEXT.ALIGN,
      maxWidth: PDF_LAYOUT.CONTENT.TITLE.MAX_WIDTH,
      baseline: 'top',
    },
  );

  // Apply layout-specific content formatting
  applySlideLayout(
    doc,
    slide,
    { fontId: font, textColor: contentTextColor },
    false,
    titleLongCheck
  );
}

// Main export
export const downloadPpt = {
  pptx: ({ slides, selectedTheme, selectedFont }: DownloadPptProps) => {
    if (!slides?.length) return;

    const theme = PPT_MAKER_THEMES[selectedTheme.id as ThemeType];
    const pptx = new PptxGenJS();

    // Title slide
    createPptxTitleSlide(pptx, slides[0], theme, selectedFont.id);

    // Content slides
    slides.slice(1).forEach((slide, index) => {
      createPptxContentSlide(
        pptx,
        slide,
        theme,
        selectedFont.id,
        index,
        slides.length - 1,
        slides,
        selectedTheme.id,
      );
    });

    pptx.writeFile({ fileName: `${slides[0].title}.pptx` });
  },

  pdf: ({ slides, selectedTheme, selectedFont }: DownloadPptProps) => {
    if (!slides?.length) return;

    const theme = PPT_MAKER_THEMES[selectedTheme.id as ThemeType];
    const font = PPT_MAKER_FONT_OPTIONS[selectedFont.id];

    const doc = new jsPDF({
      orientation: PDF_LAYOUT.PAGE.ORIENTATION,
      unit: PDF_LAYOUT.PAGE.UNIT,
      format: [PDF_LAYOUT.PAGE.WIDTH, PDF_LAYOUT.PAGE.HEIGHT],
    });

    // Title slide
    createPdfTitleSlide(doc, slides[0], theme, font);
    doc.addPage();

    // Content slides
    slides.slice(1).forEach((slide, index) => {
      createPdfContentSlide(
        doc, 
        slide, 
        theme, 
        font, 
        index, 
        slides.length - 1, 
        slides, 
        selectedTheme.id
      );

      if (index < slides.length - 2) {
        doc.addPage();
      }
    });

    doc.save(`${slides[0].title}.pdf`);
  },
};
